import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { ExternalAuthService } from '../auth/external-auth.service';
import { MonitoringService } from '../monitoring/monitoring.service';
import { TurnstileChallengeOverlay } from './turnstile-challenge-overlay';

declare global {
	interface Window {
		turnstile?: {
			render: (
				container: HTMLElement,
				options: TurnstileRenderOptions
			) => string;
			remove: (widgetId: string) => void;
		};
	}
}

interface TurnstileRenderOptions {
	sitekey: string;
	appearance?: 'always' | 'execute' | 'interaction-only';
	theme?: 'light' | 'dark' | 'auto';
	size?: 'normal' | 'compact' | 'flexible';
	callback?: (token: string) => void;
	'error-callback'?: (errorCode?: string) => void;
	'timeout-callback'?: () => void;
	'before-interactive-callback'?: () => void;
	'after-interactive-callback'?: () => void;
	'unsupported-callback'?: () => void;
}

/**
 * Desenlace de una emisión de token inicial.
 *
 * La distinción entre los dos fallos es el punto del enum: `VERIFICATION_FAILED`
 * es un problema del navegador del socio (Cloudflare no pudo ejecutar el reto) y
 * se recupera reintentando; `ISSUER_FAILED` es el backend negando la credencial,
 * que sí termina el proceso. Tratarlos igual hacía que un fallo del widget
 * terminara pintándole al socio una pantalla de crédito rechazado.
 */
export enum TurnstileOutcome {
	SUCCESS = 'SUCCESS',
	VERIFICATION_FAILED = 'VERIFICATION_FAILED',
	ISSUER_FAILED = 'ISSUER_FAILED',
}

type ChallengeResult =
	| { status: 'TOKEN'; token: string }
	| { status: 'ERROR'; code?: string }
	| { status: 'TIMEOUT' }
	| { status: 'UNSUPPORTED' };

@Injectable({
	providedIn: 'root',
})
export class TurnstileAuthService {
	private readonly MAX_RETRIES = 20;
	private readonly RETRY_INTERVAL_MS = 300;

	/** Ventana para un reto que Cloudflare resuelve solo, sin pedir interacción. */
	private readonly CHALLENGE_TIMEOUT_MS = 15000;

	/**
	 * Ventana mientras el socio resuelve el reto a mano. Los 10s que había antes
	 * no alcanzaban ni para leer el widget, así que un reto interactivo se
	 * cancelaba solo antes de que nadie pudiera tocarlo.
	 */
	private readonly INTERACTIVE_TIMEOUT_MS = 120000;

	private readonly MAX_ATTEMPTS = 3;
	private readonly BACKOFF_MS = [500, 1500];

	/**
	 * Cloudflare documenta 300*** y 600*** como fallos genéricos reintentables.
	 * El resto (110200 dominio no autorizado, 110100 sitekey inválida, 105***
	 * problemas de carga) son de configuración: reintentar no los arregla.
	 */
	private readonly RETRYABLE_ERROR_PREFIXES = ['300', '600'];

	private readonly overlay = new TurnstileChallengeOverlay();

	constructor(
		private externalAuthService: ExternalAuthService,
		private monitoringService: MonitoringService
	) {}

	/**
	 * Ejecuta el reto y emite el token inicial. Nunca rechaza la promise: el
	 * resultado viaja en el `TurnstileOutcome` para que quien llama pueda
	 * distinguir un fallo de verificación de uno de autorización.
	 */
	async initialize(): Promise<TurnstileOutcome> {
		const available = await this.waitForTurnstile();
		if (!available) {
			this.report('script-no-disponible');
			return TurnstileOutcome.VERIFICATION_FAILED;
		}

		for (let attempt = 0; attempt < this.MAX_ATTEMPTS; attempt++) {
			const result = await this.runChallenge();

			if (result.status === 'TOKEN') {
				return this.exchangeToken(result.token);
			}

			this.report('reto-fallido', {
				errorCode: result.status === 'ERROR' ? result.code : result.status,
				attempt: attempt + 1,
			});

			if (!this.isRetryable(result) || attempt === this.MAX_ATTEMPTS - 1) {
				return TurnstileOutcome.VERIFICATION_FAILED;
			}

			await this.wait(this.BACKOFF_MS[attempt] ?? 1500);
		}

		return TurnstileOutcome.VERIFICATION_FAILED;
	}

	/**
	 * Igual que `initialize`, pero si los intentos automáticos se agotan le
	 * ofrece al socio reintentar en vez de darse por vencido. Es el camino de la
	 * reemisión a media sesión, donde rendirse significa perder el avance del
	 * proceso.
	 */
	async initializeWithManualRetry(): Promise<TurnstileOutcome> {
		const outcome = await this.initialize();
		if (outcome !== TurnstileOutcome.VERIFICATION_FAILED) {
			return outcome;
		}
		return this.awaitManualRetry();
	}

	private awaitManualRetry(): Promise<TurnstileOutcome> {
		return new Promise<TurnstileOutcome>(resolve => {
			this.overlay.showRetry(
				'No pudimos completar la verificación de seguridad de tu navegador. Inténtalo de nuevo.',
				() => {
					this.monitoringService.captureAction('turnstile-reintento-manual');
					this.initialize().then(retryOutcome => {
						if (retryOutcome === TurnstileOutcome.VERIFICATION_FAILED) {
							this.awaitManualRetry().then(resolve);
							return;
						}
						this.overlay.setIdle();
						resolve(retryOutcome);
					});
				}
			);
		});
	}

	private async exchangeToken(token: string): Promise<TurnstileOutcome> {
		this.overlay.setIdle();
		try {
			await this.externalAuthService.fetchInitialToken(token).toPromise();
			return TurnstileOutcome.SUCCESS;
		} catch (error) {
			// El reto pasó: si el emisor no entrega token el problema es de
			// autorización, no de verificación. AuthTokenInterceptor ya lleva ese
			// caso a la pantalla de rechazo por su cuenta.
			this.report('emisor-rechazo', { error });
			return TurnstileOutcome.ISSUER_FAILED;
		}
	}

	/**
	 * Monta el widget y espera un único desenlace. El widget se desmonta siempre
	 * antes de resolver, y el host se reutiliza en lugar de destruirse: quitar el
	 * contenedor del DOM con el widget todavía vivo era lo que dejaba a Turnstile
	 * lanzando "Nothing to reset found for provided container" desde sus timers.
	 */
	private runChallenge(): Promise<ChallengeResult> {
		return new Promise<ChallengeResult>(resolve => {
			const host = this.overlay.widgetHost();
			let widgetId: string | undefined;
			let settled = false;
			let timeoutId: ReturnType<typeof setTimeout>;

			const finish = (result: ChallengeResult) => {
				if (settled) {
					return;
				}
				settled = true;
				clearTimeout(timeoutId);
				this.removeWidget(widgetId);
				this.overlay.setIdle();
				resolve(result);
			};

			const arm = (ms: number) => {
				clearTimeout(timeoutId);
				timeoutId = setTimeout(() => finish({ status: 'TIMEOUT' }), ms);
			};

			arm(this.CHALLENGE_TIMEOUT_MS);

			try {
				widgetId = window.turnstile!.render(host, {
					sitekey: environment.turnstileSiteKey,
					appearance: 'interaction-only',
					callback: token => finish({ status: 'TOKEN', token }),
					'error-callback': errorCode =>
						finish({ status: 'ERROR', code: errorCode }),
					'timeout-callback': () => finish({ status: 'TIMEOUT' }),
					'unsupported-callback': () => finish({ status: 'UNSUPPORTED' }),
					// Cloudflare avisa antes de pedir interacción: ese es el momento de
					// hacer visible el widget, y de ampliar la ventana para darle tiempo
					// real al socio de resolverlo.
					'before-interactive-callback': () => {
						this.overlay.setInteractive();
						arm(this.INTERACTIVE_TIMEOUT_MS);
					},
					'after-interactive-callback': () => {
						this.overlay.setIdle();
						arm(this.CHALLENGE_TIMEOUT_MS);
					},
				});
			} catch (error) {
				this.report('render-lanzo-excepcion', { error });
				finish({ status: 'ERROR', code: 'render-exception' });
			}
		});
	}

	private removeWidget(widgetId: string | undefined): void {
		if (!widgetId) {
			return;
		}
		try {
			window.turnstile?.remove(widgetId);
		} catch (error) {
			this.report('remove-lanzo-excepcion', { error });
		}
	}

	private isRetryable(result: ChallengeResult): boolean {
		if (result.status === 'TIMEOUT') {
			return true;
		}
		if (result.status !== 'ERROR') {
			return false;
		}
		const code = result.code;
		return (
			!code || this.RETRYABLE_ERROR_PREFIXES.some(prefix => code.startsWith(prefix))
		);
	}

	private waitForTurnstile(): Promise<boolean> {
		return new Promise<boolean>(resolve => {
			let attempts = 0;
			const check = () => {
				if (window.turnstile) {
					resolve(true);
					return;
				}
				attempts++;
				if (attempts >= this.MAX_RETRIES) {
					resolve(false);
					return;
				}
				setTimeout(check, this.RETRY_INTERVAL_MS);
			};
			check();
		});
	}

	private wait(ms: number): Promise<void> {
		return new Promise<void>(resolve => setTimeout(resolve, ms));
	}

	/**
	 * El código de error de Cloudflare es el único dato que dice por qué falló el
	 * reto. Antes se descartaba, y en producción no quedaba forma de saber si era
	 * 600010 (bot detectado), 110200 (dominio no autorizado) u otro.
	 */
	private report(event: string, tags: { [key: string]: unknown } = {}): void {
		console.warn(`[Auth] Turnstile: ${event}`, tags);
		this.monitoringService.captureError(new Error(`[Turnstile] ${event}`), {
			location: 'TurnstileAuthService',
			sitekey: environment.turnstileSiteKey,
			...tags,
		});
	}
}
